import * as React from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/hooks/use-auth";
import { apiErrorMessage } from "@/lib/api";
import { Globe } from "lucide-react";

export function LoginPage(): React.ReactNode {
    const { admin, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Pre-fill local-dev defaults only in development. On prod the form is blank.
    const isDev = import.meta.env.DEV;
    const [email, setEmail] = React.useState(isDev ? "admin@local" : "");
    const [password, setPassword] = React.useState(isDev ? "admin" : "");
    const [error, setError] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);

    // Already logged in? Redirect out of /login.
    if (admin) {
        const target = (location.state as { from?: string } | null)?.from || "/";
        return <Navigate to={target} replace />;
    }

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await login(email, password);
            const target = (location.state as { from?: string } | null)?.from || "/";
            navigate(target, { replace: true });
        } catch (err) {
            setError(apiErrorMessage(err, "Login failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
                            <Globe className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle>Landing Manager</CardTitle>
                            <CardDescription>Admin console</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <form onSubmit={handleSubmit} noValidate>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={submitting}
                                placeholder="admin@local"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={submitting}
                            />
                        </div>
                        {error && (
                            <div
                                role="alert"
                                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                            >
                                {error}
                            </div>
                        )}
                    </CardContent>
                    <CardFooter className="flex flex-col gap-3">
                        <Button type="submit" className="w-full" disabled={submitting || !email || !password}>
                            {submitting ? <Spinner size="sm" /> : "Sign in"}
                        </Button>
                        {isDev && (
                            <p className="text-center text-xs text-muted-foreground">
                                Local dev credentials pre-filled (admin@local / admin).
                            </p>
                        )}
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
